/* ============================================================================
 * WARRANTY CART AUTOMATION ENGINE (Aggressive re‑offer version)
 * ----------------------------------------------------------------------------
 * Goals:
 * - Never assume cart state.
 * - Always re‑read /cart.js after cart writes.
 * - Enforce exactly one warranty line, quantity = 1.
 * - Remove wrong tiers, duplicates, and stale warranty lines.
 * - Remove warranty automatically when no eligible merchandise remains.
 * - Respect customer opt‑out, but only while the eligible items remain
 *   exactly the same as when the warranty was last removed.
 * - Keep cart drawer / sections refreshed when the theme requests sections.
 *  *  Added by Teandrils on 7/28/2026 to add protection plan to cart automatically. looks for warranty-cart files
 * ========================================================================== */

(function () {
  'use strict';

  var DEBUG = true;
  var MAX_RECONCILE_PASSES = 8;
  var OPT_OUT_KEY = '_warranty_opt_out';
  var SNAPSHOT_KEY = '_warranty_opt_out_snapshot';   // <-- NEW

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ['[warranty]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function toNumber(value, fallback) {
    var n = Number(value);
    return isFinite(n) ? n : fallback;
  }

  function isBlank(value) {
    return value === '' || value == null || String(value).toLowerCase() === 'null';
  }

  function readJson(response) {
    return response.text().then(function (text) {
      var data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = {};
        }
      }
      if (!response.ok) {
        var err = new Error('HTTP ' + response.status);
        err.response = response;
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  var CFG = window.WARRANTY_CONFIG;
  if (!CFG || !Array.isArray(CFG.tiers) || !CFG.tiers.length) {
    console.warn('[warranty] no WARRANTY_CONFIG');
    return;
  }

  var TIERS = CFG.tiers
    .map(function (t) {
      return {
        min: toNumber(t && t.min, 0),
        max: isBlank(t && t.max) ? null : toNumber(t.max, null),
        variantId: toNumber(t && t.variantId, 0),
      };
    })
    .filter(function (t) {
      return !!t.variantId;
    })
    .sort(function (a, b) {
      return a.min - b.min;
    });

  if (!TIERS.length) {
    console.warn('[warranty] no valid warranty tiers');
    return;
  }

  var WARRANTY_IDS = TIERS.map(function (t) {
    return t.variantId;
  });

  var state = {
    internalDepth: 0,
    running: false,
    queued: false,
    pendingExternalWrite: false,
    lastKnownWarrantyPresent: false,
  };

  var _fetch = window.fetch;
  var _open = XMLHttpRequest.prototype.open;

  function isWarrantyVariantId(id) {
    return WARRANTY_IDS.indexOf(toNumber(id, -1)) !== -1;
  }

  function getItems(cart) {
    return (cart && Array.isArray(cart.items)) ? cart.items : [];
  }

  function getWarrantyLines(cart) {
    return getItems(cart).filter(function (line) {
      return isWarrantyVariantId(line && line.id);
    });
  }

  function hasWarranty(cart) {
    return getWarrantyLines(cart).length > 0;
  }

  function warrantyCount(cart) {
    return getWarrantyLines(cart).reduce(function (sum, line) {
      return sum + toNumber(line && line.quantity, 0);
    }, 0);
  }

  function eligibleSubtotalCents(cart) {
    return getItems(cart).reduce(function (sum, line) {
      if (isWarrantyVariantId(line && line.id)) return sum;
      var cents = toNumber(line && line.final_line_price, null);
      if (cents == null) cents = toNumber(line && line.line_price, 0);
      return sum + cents;
    }, 0);
  }

  // --- NEW: create a snapshot of eligible items (key|qty) ---
  function getEligibleSnapshot(cart) {
    var items = getItems(cart).filter(function (line) {
      return !isWarrantyVariantId(line && line.id);
    });
    if (!items.length) return '';
    // sort by key|qty string to make snapshot deterministic
    items.sort(function (a, b) {
      var sa = (a.key || '') + '|' + a.quantity;
      var sb = (b.key || '') + '|' + b.quantity;
      return sa.localeCompare(sb);
    });
    return items.map(function (line) {
      return (line.key || '') + '|' + line.quantity;
    }).join(',');
  }

  function matchTierVariant(cents) {
    var subtotal = cents / 100;
    for (var i = 0; i < TIERS.length; i++) {
      var t = TIERS[i];
      if (subtotal >= t.min && (t.max == null || subtotal <= t.max)) {
        return t.variantId;
      }
    }
    return null;
  }

  function getCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(readJson);
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(readJson);
  }

  function setAttr(key, value) {
    var attrs = {};
    attrs[key] = value;
    return post('/cart/update.js', { attributes: attrs });
  }

  function removeLine(key) {
    return post('/cart/change.js', { id: key, quantity: 0 });
  }

  function addWarranty(variantId) {
    return post('/cart/add.js', {
      items: [{ id: variantId, quantity: 1 }],
    });
  }

  function parseBody(init) {
    try {
      if (!init || !init.body) return null;
      var b = init.body;

      if (typeof b === 'string') {
        try {
          return JSON.parse(b);
        } catch (e) {
          return null;
        }
      }

      if (typeof FormData !== 'undefined' && b instanceof FormData) {
        var o = {};
        b.forEach(function (v, k) { o[k] = v; });
        return o;
      }

      if (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams) {
        var o2 = {};
        b.forEach(function (v, k) { o2[k] = v; });
        return o2;
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  function waitForIdle() {
    return new Promise(function (resolve) {
      (function tick() {
        if (!state.running && !state.queued) return resolve();
        setTimeout(tick, 0);
      })();
    });
  }

  function refreshSectionsIfRequested(originalResp, init) {
    var body = parseBody(init);
    if (!body) return Promise.resolve(originalResp);

    var sections = body.sections;
    if (Array.isArray(sections)) sections = sections.join(',');

    if (typeof sections !== 'string' || !sections) {
      return Promise.resolve(originalResp);
    }

    return runInternal(function () {
      return post('/cart/update.js', {
        sections: sections,
        sections_url: body.sections_url || window.location.pathname,
      });
    }).then(function (fresh) {
      return originalResp.clone().text().then(function (text) {
        var out = {};
        if (text) {
          try {
            out = JSON.parse(text);
          } catch (e) {
            out = {};
          }
        }

        out.sections = fresh.sections;
        if (typeof fresh.item_count !== 'undefined') {
          out.item_count = fresh.item_count;
        }

        return new Response(JSON.stringify(out), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }).catch(function () {
        return originalResp;
      });
    }).catch(function () {
      return originalResp;
    });
  }

  function scheduleSync(reason) {
    log('schedule', reason);
    if (state.running) {
      state.queued = true;
      return;
    }

    state.queued = true;
    Promise.resolve().then(processQueue);
  }

  function beginInternal() {
    state.internalDepth++;
  }

  function endInternal() {
    state.internalDepth = Math.max(0, state.internalDepth - 1);

    if (state.internalDepth === 0 && state.pendingExternalWrite) {
      state.pendingExternalWrite = false;
      scheduleSync('pending-external');
    }
  }

  function runInternal(task) {
    beginInternal();
    return Promise.resolve()
      .then(task)
      .then(
        function (result) {
          endInternal();
          return result;
        },
        function (err) {
          endInternal();
          throw err;
        }
      );
  }

  function normalizeOnce(cart) {
    var attrs = (cart && cart.attributes) || {};
    var optedOut = attrs[OPT_OUT_KEY] === 'true';
    var warrantyLines = getWarrantyLines(cart);
    var present = warrantyLines.length > 0;
    var eligible = eligibleSubtotalCents(cart);
    var desired = (!optedOut && eligible > 0) ? matchTierVariant(eligible) : null;

    log(
      'subtotal $' + (eligible / 100).toFixed(2),
      '| count', warrantyCount(cart),
      '| current', warrantyLines.map(function (l) { return l.id; }).join(',') || 'none',
      '| desired', desired,
      '| optedOut', optedOut
    );

    // --- NEW: clear opt‑out if the eligible items have changed since opt‑out ---
    if (optedOut && !present) {
      var currentSnap = getEligibleSnapshot(cart);
      var storedSnap = attrs[SNAPSHOT_KEY];
      if (storedSnap !== undefined && storedSnap !== currentSnap) {
        log('eligible items changed – clearing opt‑out and snapshot');
        return runInternal(function () {
          return setAttr(OPT_OUT_KEY, '').then(function () {
            return setAttr(SNAPSHOT_KEY, '');
          });
        }).then(function () {
          // The next pass will see optedOut == false and add the correct warranty
          return true;
        });
      }
    }

    // Manual removal detection: if we previously had a warranty, now we don't,
    // and eligible merchandise still exists, mark opt-out (and store snapshot).
    if (!state.internalDepth && state.lastKnownWarrantyPresent && !present && !optedOut && eligible > 0) {
      return runInternal(function () {
        var snap = getEligibleSnapshot(cart);
        log('manual warranty removal detected -> opt out with snapshot', snap);
        return setAttr(OPT_OUT_KEY, 'true').then(function () {
          return setAttr(SNAPSHOT_KEY, snap);
        }).then(function () {
          return true;
        });
      });
    }

    // If the customer re-added a warranty while opted out, clear the opt-out.
    if (!state.internalDepth && optedOut && present) {
      return runInternal(function () {
        log('warranty present while opted out -> clear opt out');
        return setAttr(OPT_OUT_KEY, '').then(function () {
          return setAttr(SNAPSHOT_KEY, '');      // <-- also clear snapshot
        }).then(function () {
          return true;
        });
      });
    }

    // If no warranty should exist, remove every warranty line.
    if (desired == null) {
      if (!present) {
        state.lastKnownWarrantyPresent = false;
        return Promise.resolve(false);
      }

      return runInternal(function () {
        log('no desired warranty -> remove all warranty lines');
        var chain = Promise.resolve();
        warrantyLines.forEach(function (line) {
          chain = chain.then(function () {
            return removeLine(line.key);
          });
        });
        return chain.then(function () {
          return true;
        });
      });
    }

    // Exact valid state: one warranty line, correct variant, qty 1.
    var exact =
      warrantyLines.length === 1 &&
      toNumber(warrantyLines[0].id, -1) === desired &&
      toNumber(warrantyLines[0].quantity, 0) === 1;

    if (exact) {
      state.lastKnownWarrantyPresent = true;
      return Promise.resolve(false);
    }

    // Normalize: remove all warranty lines, then add exactly one desired warranty line.
    return runInternal(function () {
      log('normalize warranty -> replace all warranty lines with', desired);

      var chain = Promise.resolve();

      warrantyLines.forEach(function (line) {
        chain = chain.then(function () {
          return removeLine(line.key);
        });
      });

      chain = chain.then(function () {
        return addWarranty(desired);
      });

      return chain.then(function () {
        return true;
      });
    });
  }

  function reconcileToStable(cart, pass) {
    pass = pass || 0;

    if (pass >= MAX_RECONCILE_PASSES) {
      state.lastKnownWarrantyPresent = hasWarranty(cart);
      log('max reconcile passes reached');
      return Promise.resolve(false);
    }

    return normalizeOnce(cart).then(function (changed) {
      if (!changed) {
        state.lastKnownWarrantyPresent = hasWarranty(cart);
        return false;
      }

      return getCart().then(function (fresh) {
        return reconcileToStable(fresh, pass + 1);
      });
    }).catch(function (err) {
      log('reconcile error', err && err.message ? err.message : err);
      return false;
    });
  }

  function processQueue() {
    if (state.running) return Promise.resolve();

    state.running = true;
    state.queued = false;

    return Promise.resolve()
      .then(function loop() {
        return getCart()
          .then(function (cart) {
            return reconcileToStable(cart, 0);
          })
          .then(function () {
            if (state.queued) {
              state.queued = false;
              return loop();
            }
            return false;
          });
      })
      .catch(function (err) {
        log('queue error', err && err.message ? err.message : err);
        return false;
      })
      .finally(function () {
        state.running = false;
      });
  }

  // Fetch interception: schedule a resync after any external cart write.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?/;

  window.fetch = function () {
    var args = arguments;
    var a0 = args[0];
    var url = (a0 && a0.url) ? a0.url : String(a0);

    if (!CART_WRITE.test(url)) {
      return _fetch.apply(this, args);
    }

    var init = args[1];

    return _fetch.apply(this, args).then(function (resp) {
      if (state.internalDepth > 0) {
        state.pendingExternalWrite = true;
        return resp;
      }

      scheduleSync('fetch ' + url);

      return waitForIdle().then(function () {
        return refreshSectionsIfRequested(resp, init);
      });
    }).catch(function (err) {
      if (state.internalDepth > 0) {
        state.pendingExternalWrite = true;
      } else {
        scheduleSync('fetch error ' + url);
      }
      throw err;
    });
  };

  // XHR fallback for themes that use XMLHttpRequest.
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener('load', function () {
      if (!CART_WRITE.test(String(url))) return;

      if (state.internalDepth > 0) {
        state.pendingExternalWrite = true;
        return;
      }

      scheduleSync('xhr ' + url);
    });

    return _open.apply(this, arguments);
  };

  // Initial sync.
  scheduleSync('initial load');
})();